import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * shadcn's Progress, without the Radix dependency.
 *
 * `@radix-ui/react-progress` is not installed in this workspace, and the
 * primitive contributes nothing but the ARIA attributes written out below — it
 * has no focus management, no portal and no state machine. Rather than
 * hand-rolling a different bar, this keeps shadcn's exact markup, class names
 * and `translateX` indicator so a later `shadcn add progress` is a clean
 * replacement rather than a redesign.
 *
 * `value` is a percentage. `null` renders an indeterminate bar, which is the
 * honest state while the server is unpacking an archive it has already received.
 */
export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: number | null
  label?: string
}

const Progress = React.forwardRef<HTMLDivElement, ProgressProps>(
  ({ className, value = null, label, ...props }, ref) => {
    const determinate = typeof value === "number" && Number.isFinite(value)
    const percent = determinate ? Math.min(100, Math.max(0, value)) : null

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(percent === null ? {} : { "aria-valuenow": Math.round(percent) })}
        {...(label === undefined ? {} : { "aria-label": label })}
        className={cn(
          "relative h-2 w-full overflow-hidden rounded-full bg-primary/20",
          className
        )}
        {...props}
      >
        <div
          className={cn(
            "h-full w-full flex-1 bg-primary transition-transform duration-300 ease-out",
            percent === null && "animate-pulse"
          )}
          style={{ transform: `translateX(-${String(100 - (percent ?? 40))}%)` }}
        />
      </div>
    )
  }
)
Progress.displayName = "Progress"

export { Progress }
